import { Injectable }                  from '@angular/core';
import {
	ACCOUNT_TENANT_METADATA,
	IAccountTenantMetadata
}                                      from '@app/modules/admin-tenant/config/account-tenant.config';
import {
	ADMIN_TENANT_METADATA,
	IAdminTenantMetadata
}                                      from '@app/modules/admin-tenant/config/admin-tenant.config';
import { DISTRIBUTOR_TENANT_METADATA } from '@app/modules/admin-tenant/config/distributor-tenant.config';
import {
	IPartnerTenantMetadata,
	PARTNER_TENANT_METADATA
}                                      from '@app/modules/admin-tenant/config/partner-tenant.config';
import { RESELLER_TENANT_METADATA }    from '@app/modules/admin-tenant/config/reseller-tenant.config';
import {
	CapabilitiesEnum,
	Tenant,
	TenantContractLog
}                                      from '@app/modules/admin-tenant/models/tenant.model';
import { AccountTenantService }        from '@app/modules/admin-tenant/services/account-tenant.service';
import { AdminTenantService }          from '@app/modules/admin-tenant/services/admin-tenant.service';
import { ProductEnum }                 from '@core/interfaces/common.interfaces';
import { ProductHelperService }        from '@core/services/product-helper.service';
import {
	RoleTypes,
	TenantsManagingAdminRoleTypes
}                                      from '@shared/models/roles.model';
import { CurrentUserService }          from '@shared/services/current-user.service';
import { LoggerService }               from '@shared/services/logger.service';
import { DateUtils }                   from '@utils/helpers';
import { DateRelationEnum }            from '@utils/helpers/date-utils';

@Injectable( { providedIn: 'root' } )
export class TenantHelperService {

	private readonly accountTenantMetadata = ACCOUNT_TENANT_METADATA;
	private readonly adminTenantMetadata = ADMIN_TENANT_METADATA;
	private readonly distributorTenantMetadata = DISTRIBUTOR_TENANT_METADATA;
	private readonly partnerTenantMetadata = PARTNER_TENANT_METADATA;
	private readonly resellerTenantMetadata = RESELLER_TENANT_METADATA;

	private _currentUsersTenantManagingAdminRole: TenantsManagingAdminRoleTypes;
	private _roleBasedTenantMetadata: IAdminTenantMetadata | IPartnerTenantMetadata | IAccountTenantMetadata;
	private _roleBasedTenantService;

	// TODO workaround solution for sub-tenant list -> needs own store space
	public subTenantList: Tenant[];

	constructor(
		private readonly _accountTenantService: AccountTenantService,
		private readonly _adminTenantService: AdminTenantService,
		private readonly _currentUserService: CurrentUserService,
		private readonly _logger: LoggerService,
		private readonly _productHelperService: ProductHelperService
	) {
		this._logger.trace( 'TenantHelperService constructor' );
	}

	getProductData<archive, elster, fiskal>(
		product: ProductEnum,
		archiveValue: archive,
		elsterValue: elster,
		fiskalValue: fiskal
	): archive | elster | fiskal {
		return this._productHelperService.getProductData<archive, elster, fiskal>(
			product,
			archiveValue,
			elsterValue,
			fiskalValue
		);
	}

	get currentUsersTenantBasedPageTitle(): string {
		return this.currentUsersTenantManagingAdminRole.toLowerCase().replace( /[-_][a-z]/g, ( group ) => group.slice( -1 ).toUpperCase() ) + 'Title';
	}

	get currentUsersTenantManagingAdminRole() {
		return this._currentUsersTenantManagingAdminRole
					 ? this._currentUsersTenantManagingAdminRole
					 : this._currentUserService.getCurrentUsersTenantAdminRole();
	}

	get currentUsersRoleBasedTenantService(): AdminTenantService | AccountTenantService {
		if ( !this._roleBasedTenantService ) {
			switch ( this.currentUsersTenantManagingAdminRole ) {
				case RoleTypes.SUPPORT_ADMIN:
				case RoleTypes.SUPPORT_ADMIN_VIEW:
					this._roleBasedTenantService = this._adminTenantService;
					break;

				default:
					this._roleBasedTenantService = this._accountTenantService;
					break;
			}
		}

		return this._roleBasedTenantService;
	}

	get currentUsersRoleBasedTenantMetadata() {

		if ( !this._roleBasedTenantMetadata ) {
			switch ( this.currentUsersTenantManagingAdminRole ) {
				case RoleTypes.SUPPORT_ADMIN:
				case RoleTypes.SUPPORT_ADMIN_VIEW:
					this._roleBasedTenantMetadata = this.adminTenantMetadata;
					break;
				case RoleTypes.RESELLER_ADMIN:
					this._roleBasedTenantMetadata = this.resellerTenantMetadata;
					break;
				case RoleTypes.DISTRIBUTOR_ADMIN:
					this._roleBasedTenantMetadata = this.distributorTenantMetadata;
					break;
				case RoleTypes.VENDOR_ADMIN:
				case RoleTypes.PARTNER_ADMIN:
					this._roleBasedTenantMetadata = this.partnerTenantMetadata;
					break;
				case RoleTypes.ACCOUNT_ADMIN:
					this._roleBasedTenantMetadata = this.accountTenantMetadata;
					break;
			}
		}
		return this._roleBasedTenantMetadata;
	}


	isFirstTenantContractLogItemAFutureChange( tenantContractLog: TenantContractLog[] ): boolean {
		if ( tenantContractLog?.length > 1 ) {
			return DateUtils.getDateRelation( tenantContractLog[ 0 ].from, new Date() ) === DateRelationEnum.IS_FUTURE;
		}
		return false;
	}

	getCurrentTenantContractLogItem( tenantContractLog: TenantContractLog[] ): TenantContractLog {
		if ( tenantContractLog?.length > 1 ) {
			const isFirstItemFutureLog = this.isFirstTenantContractLogItemAFutureChange( tenantContractLog );
			return isFirstItemFutureLog ? tenantContractLog[ 1 ] : tenantContractLog[ 0 ];
		}
		if ( tenantContractLog?.length === 1 ) {
			return tenantContractLog[ 0 ];
		}
		return null;
	}

	getCurrentTenantContractLog( tenantContractLog: TenantContractLog[] ): TenantContractLog {
		if ( !tenantContractLog || tenantContractLog?.length === 0 ) {
			return null;
		}
		const isFirstItemFutureLog = DateUtils.getDateRelation( tenantContractLog[ 0 ].from, new Date() ) === DateRelationEnum.IS_FUTURE;
		return isFirstItemFutureLog ? tenantContractLog[ 1 ] : tenantContractLog[ 0 ];
	}

	setCapabilities( tenant: Tenant ): Tenant {
		if ( tenant && tenant.systemProperties ) {
			const capabilities: CapabilitiesEnum[] = [];
			if ( tenant && tenant?.systemProperties?.archiving?.enabled ) {
				capabilities.push( CapabilitiesEnum.ARCHIVING );
			}
			tenant = { ...tenant, capabilities };
		}

		return tenant;
	}
}
